// Normalized property data schema
// All platform services (Bazaraki, BuySell, etc.) MUST return this shape
// from their fetch functions. This decouples useProperties.js and
// PropertiesContainer.jsx from any platform-specific API format.

/**
 * Creates a normalized property data object with safe defaults.
 * Platform services should call this to ensure consistent output.
 * 
 * @param {Object} fields - Partial normalized fields
 * @returns {Object} - Full normalized property data
 */
export const DEFAULT_DISTRESS = {
  acquired: false,
  auction: false,
  reducedPrice: false,
  startPrice: null,
  auctionDate: null,
  deposit: null,
};

/**
 * Pick the distressed-asset signals from a source object (normalized fetch data
 * or persisted additionalFields), applying safe defaults and preserving any
 * existing stored values via `fallback`. Used by the persistence layer so
 * vendor/auction/bank-owned badges always have data to render.
 *
 * @param {Object} source - normalized data or additionalFields
 * @param {Object} [fallback] - existing stored property values to preserve
 * @returns {{currency: string, priceOnRequest: boolean, sellerType: string, distress: Object, isDistressed: boolean}}
 */
export const extractDistressFields = (source = {}, fallback = {}) => {
  const distress = source.distress || fallback.distress || DEFAULT_DISTRESS;
  return {
    currency: (source.currency || fallback.currency || "EUR").toUpperCase(),
    priceOnRequest: source.priceOnRequest ?? fallback.priceOnRequest ?? false,
    sellerType: source.sellerType || fallback.sellerType || "unknown",
    distress,
    isDistressed:
      source.isDistressed ??
      !!(distress.acquired || distress.auction || distress.reducedPrice),
  };
};

export const createNormalizedPropertyData = (fields = {}) => {
  const distress = {
    acquired: !!(fields.distress?.acquired),
    auction: !!(fields.distress?.auction),
    reducedPrice: !!(fields.distress?.reducedPrice),
    startPrice: typeof fields.distress?.startPrice === 'number' ? fields.distress.startPrice : null,
    auctionDate: fields.distress?.auctionDate || null,
    deposit: typeof fields.distress?.deposit === 'number' ? fields.distress.deposit : null,
  };

  return {
  // Core info
  title: fields.title || "",
  description: fields.description || "",
  price: typeof fields.price === 'number' ? fields.price : parseFloat(fields.price) || 0,
  currency: (fields.currency || "EUR").toUpperCase(),
  priceOnRequest: !!fields.priceOnRequest,
  startPrice: fields.startPrice || "",

  // Dates
  publishedDate: fields.publishedDate || "",
  updatedDate: fields.updatedDate || "",

  // Property details
  area: fields.area || "",
  bedrooms: fields.bedrooms || "",
  bathrooms: fields.bathrooms || "",

  // Media
  imageURL: fields.imageURL || "",

  // Location
  latitude: fields.latitude ?? null,
  longitude: fields.longitude ?? null,
  cityId: fields.cityId || "",
  location: fields.location || "",

  // Attributes
  pets: fields.pets || "N/A",
  furnishing: fields.furnishing || "N/A",
  parking: fields.parking || "N/A",
  constructionYear: fields.constructionYear || "N/A",
  onlineViewing: fields.onlineViewing || "N/A",

  // Owner/Agent info
  landlordName: fields.landlordName || "",
  landlordJoined: fields.landlordJoined || "",
  ownerAdCount: fields.ownerAdCount || "",

  // Property type
  forSale: fields.forSale || false,
  forRent: fields.forRent || false,

  // Distressed-asset signals (mirrors the ingestion schema in
  // cloudflare-workers/opportunity-scanner/src/property-schema.js)
  sellerType: fields.sellerType || "unknown",
  distress,
  isDistressed: distress.acquired || distress.auction || distress.reducedPrice,
  };
};
